// hooks/useListingFilters.ts

import { useMemo, useState } from "react";

export type FilterOption = {
  value: any;
  label: string;
  title?: string;
};

export type FilterConfigItem = {
  name: string;
  label: string;
  initialValue?: FilterOption | null;
  options?: FilterOption[];
  search?: boolean;
  disabled?: boolean;
};

export type BookingFiltersState = {
  usagesType: FilterOption | null;
  property: FilterOption | null;
  unit: FilterOption | null;
};
type HandlerArgs<T> = {
  option: FilterOption;
  state: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
  setUpdateValue: React.Dispatch<any>;
};

/** Controls which listing filter rows appear in `viewOption`. All default to true. */
export type ListingFilterSections = {
  usagesType?: boolean;
  /** When false, omits both Property and Unit rows (they share the unit-selection flow). */
  units?: boolean;
  property?: boolean;
};

const defaultSections: Required<ListingFilterSections> = {
  usagesType: true,
  units: true,
  property: true,
};

function applySectionVisibility(
  items: FilterConfigItem[],
  sections: ListingFilterSections | undefined,
): FilterConfigItem[] {
  if (!sections) {
    return items;
  }
  const merged = { ...defaultSections, ...sections };
  return items.filter((item) => {
    if (item.name === "usagesType") {
      return merged.usagesType;
    }
    if (item.name === "unit") {
      return merged.units;
    }
    if (item.name === "property") {
      return merged.property;
    }
    return true;
  });
}

type UseListingFiltersProps<T> = {
  configBuilder: (state: T) => FilterConfigItem[];
  initialState: T;
  handlers?: {
    [K in keyof T]?: (args: HandlerArgs<T>) => void;
  };
  /** Omit keys to keep defaults (all sections visible). */
  sections?: ListingFilterSections;
};

export function useListingFilters<T extends Record<string, any>>({
  configBuilder,
  initialState,
  handlers = {},
  sections,
}: UseListingFiltersProps<T>) {
  const [filtersState, setFiltersState] = useState<T>(initialState);
  const [updateValue, setUpdateValue] = useState<any>({});

  const handleFilterChange = (option: FilterOption, label: keyof T) => {
   
    setFiltersState((prev) => ({
      ...prev,
      [label]: option,
    }));

    if (handlers[label]) {
      handlers[label]!({
        option,
        state: filtersState,
        setState: setFiltersState,
        setUpdateValue,
      });
    }
  };

  const viewOption = useMemo(() => {
    return applySectionVisibility(configBuilder(filtersState), sections);
  }, [filtersState, configBuilder, sections]);

  return {
    viewOption,
    handleFilterChange,
    updateValue,
    filtersState,
    setFiltersState,
  };
}
